import { Typography } from "@mui/material"
import { diplomeData } from "@/app/(editorial)/alternance/_components/diplome_data"
import { metierData } from "@/app/(editorial)/alternance/_components/metier_data"
import { villeData } from "@/app/(editorial)/alternance/_components/ville_data"
import { publicConfig } from "@/config.public"
import { PAGES } from "@/utils/routes.utils"
import { DsfrHeaderProps } from "./Header"
import { footerId, type ZoneName } from "./zone-ids"

// Deeplink officiel Google "sources préférées" (sans script tiers) :
// https://developers.google.com/search/docs/appearance/preferred-sources
// Le domaine de production est utilisé quel que soit l'environnement, car c'est lui qui est référencé par Google.
const GOOGLE_PREFERRED_SOURCE_URL = "https://www.google.com/preferences/source?q=labonnealternance.apprentissage.beta.gouv.fr"

// Les liens externes du footer restent en <a> natif : ils n'ont pas besoin de la navigation
// client, et DsfrLink tirerait next/link dans le first-load de toutes les pages. La mention
// « nouvelle fenêtre » est donc posée à la main, en littéral pour rester vérifiable par la
// règle .biome/plugins/lien-nouvelle-fenetre.grit.

// RGAA 6.1 : le lien de marque contient trois textes visibles (brandTop et les alternatives des
// deux logos). Le title du header n'en couvre que deux, il ne peut donc pas être réutilisé ici.
const FOOTER_BRAND_TITLE = "République Française - Accueil - La bonne alternance - Un service proposé par numerique.gouv"

type LinkItem = {
  linkProps: {
    href: string
  }
  text: string
  isExternal?: boolean
}

type LinkCategory = {
  categoryName: string
  links: LinkItem[]
}

const linkListContent: LinkCategory[] = [
  {
    categoryName: "Aide & Ressources",
    links: [
      {
        linkProps: {
          href: PAGES.static.planDuSite.getPath(),
        },
        text: "Plan du site",
      },
      {
        linkProps: {
          href: PAGES.static.faq.getPath(),
        },
        text: "FAQ",
      },
      {
        linkProps: {
          href: PAGES.static.guideAlternant.getPath(),
        },
        text: "Guide alternant",
      },
      {
        linkProps: {
          href: PAGES.static.guideAlternantComprendreLaRemuneration.getPath(),
        },
        text: "Salaire en alternance",
      },
      {
        linkProps: {
          href: PAGES.static.guideRecruteur.getPath(),
        },
        text: "Guide recruteur",
      },
      {
        linkProps: {
          href: PAGES.static.guideCfa.getPath(),
        },
        text: "Guide CFA",
      },
      {
        linkProps: {
          href: PAGES.static.statistiques.getPath(),
        },
        text: "Statistiques",
      },
    ],
  },
  {
    categoryName: "À propos",
    links: [
      {
        linkProps: {
          href: PAGES.static.aPropos.getPath(),
        },
        text: "À propos",
      },
      {
        linkProps: {
          href: PAGES.static.blog.getPath(),
        },
        text: "Blog",
        isExternal: true,
      },
      {
        linkProps: {
          href: PAGES.static.metiers.getPath(),
        },
        text: "Métiers",
      },
      {
        linkProps: {
          href: PAGES.static.contact.getPath(),
        },
        text: "Contact",
      },
    ],
  },
  {
    categoryName: "Développeurs",
    links: [
      {
        linkProps: {
          href: PAGES.static.EspaceDeveloppeurs.getPath(),
        },
        text: "Espace développeurs",
      },
      {
        linkProps: {
          href: PAGES.static.codeSources.getPath(),
        },
        text: `Code source v${publicConfig.version}`,
        isExternal: true,
      },
    ],
  },
  {
    categoryName: "Alternance par ville",
    links: [
      ...villeData.slice(0, 10).map((ville) => ({
        linkProps: {
          href: PAGES.dynamic.seoVille(ville.slug).getPath(),
        },
        text: `Alternance à ${ville.ville}`,
      })),
      {
        linkProps: {
          href: PAGES.static.alternanceVilles.getPath(),
        },
        text: "Voir toutes les villes en alternance",
      },
    ],
  },
  {
    categoryName: "Alternance par métier",
    links: [
      ...metierData.slice(0, 10).map((metier) => ({
        linkProps: {
          href: PAGES.dynamic.seoMetier(metier.slug).getPath(),
        },
        text: `${metier.metier}`,
      })),
      {
        linkProps: {
          href: PAGES.static.alternanceMetiers.getPath(),
        },
        text: "Voir tous les métiers en alternance",
      },
    ],
  },
  {
    categoryName: "Alternance par diplôme",
    links: [
      ...diplomeData.slice(0, 10).map((diplome) => ({
        linkProps: {
          href: PAGES.dynamic.seoDiplome(diplome.slug).getPath(),
        },
        text: diplome.titre,
      })),
      {
        linkProps: {
          href: PAGES.static.alternanceDiplomes.getPath(),
        },
        text: "Voir tous les diplômes en alternance",
      },
    ],
  },
]

export function Footer({ zone, isWidget = false }: { zone: ZoneName; isWidget?: boolean }) {
  const description =
    "La bonne alternance simplifie les mises en relation entre les trois types d’acteurs candidats, recruteurs et centres de formation, afin de faciliter les entrées en alternance."
  const widgetDescription = (
    <Typography>
      {/* variant="h6" ne porte ici que la taille : ce n'est pas un titre de section, et un <h6>
          dans le <p> parent serait de surcroît invalide (RGAA 8.2 et 8.9). */}
      <Typography component="span" variant="h6" sx={{ display: "block" }}>
        Le dépôt simplifié d'offre en alternance
      </Typography>
      La bonne alternance est un service public numérique qui simplifie les mises en relation entre les trois types d’acteurs candidats, recruteurs et centres de formation, afin de
      faciliter les entrées en alternance
    </Typography>
  )

  const showLinkList = !isWidget

  return (
    <footer className="fr-footer" role="contentinfo" id={footerId(zone)}>
      {showLinkList && (
        <nav className="fr-footer__top" role="navigation" aria-label="Liens du pied de page">
          <h2 className="fr-sr-only">Informations et liens du site</h2>
          <div className="fr-container">
            <div className="fr-grid-row fr-grid-row--gutters">
              {linkListContent.map((category, index) => (
                <div key={index} className="fr-col-12 fr-col-sm-3 fr-col-md-2">
                  <h3 className="fr-footer__top-cat">{category.categoryName}</h3>
                  <ul className="fr-footer__top-list">
                    {category.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        {link.isExternal ? (
                          <a className="fr-footer__top-link" href={link.linkProps.href as string} target="_blank" rel="noopener noreferrer">
                            {link.text}
                            <span className="fr-sr-only"> - nouvelle fenêtre</span>
                          </a>
                        ) : (
                          // Lien interne laissé en <a> : un passage à next/link ferait de ces liens
                          // une navigation SPA, avec la gestion du focus et du titre que cela implique (RGAA 12.8).
                          <a className="fr-footer__top-link" href={link.linkProps.href as string}>
                            {link.text}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </nav>
      )}
      <div className="fr-container">
        <div className="fr-footer__body">
          <div className="fr-footer__brand fr-enlarge-link" style={{ flex: "1 1 450px" }}>
            <a
              href={DsfrHeaderProps.homeLinkProps.href as string}
              // RGAA 6.1 : le title doit reprendre tout le contenu du lien, ici trois textes
              // visibles (brandTop, logo La bonne alternance, logo numerique.gouv).
              title={FOOTER_BRAND_TITLE}
              style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "flex-end" }}
            >
              <p className="fr-logo">{DsfrHeaderProps.brandTop}</p>
              {/** biome-ignore lint/performance/noImgElement: migration */}
              <img
                className="fr-footer__logo"
                src="/images/logo_LBA.svg"
                width="145"
                height="37"
                style={{ width: "145px", height: "auto", marginLeft: "auto" }}
                alt="La bonne alternance"
              />

              {/** biome-ignore lint/performance/noImgElement: migration */}
              <img
                className="fr-footer__logo"
                src="/images/logo_numerique_gouv.svg"
                width="195"
                height="55"
                style={{ width: "195px", height: "auto" }}
                alt="Un service proposé par numerique.gouv"
              />
            </a>
          </div>
          <div className="fr-footer__content" style={{ flex: "1 1 450px" }}>
            <p className="fr-footer__content-desc">{isWidget ? widgetDescription : description}</p>
            <ul className="fr-footer__content-list">
              <li className="fr-footer__content-item">
                <a className="fr-footer__content-link" href="https://info.gouv.fr/" target="_blank" rel="noopener noreferrer">
                  info.gouv.fr
                  <span className="fr-sr-only"> - nouvelle fenêtre</span>
                </a>
              </li>
              <li className="fr-footer__content-item">
                <a className="fr-footer__content-link" href="https://service-public.gouv.fr/" target="_blank" rel="noopener noreferrer">
                  service-public.gouv.fr
                  <span className="fr-sr-only"> - nouvelle fenêtre</span>
                </a>
              </li>
              <li className="fr-footer__content-item">
                <a className="fr-footer__content-link" href="https://legifrance.gouv.fr/" target="_blank" rel="noopener noreferrer">
                  legifrance.gouv.fr
                  <span className="fr-sr-only"> - nouvelle fenêtre</span>
                </a>
              </li>
              <li className="fr-footer__content-item">
                <a className="fr-footer__content-link" href="https://data.gouv.fr" target="_blank" rel="noopener noreferrer">
                  data.gouv.fr
                  <span className="fr-sr-only"> - nouvelle fenêtre</span>
                </a>
              </li>
            </ul>
            {!isWidget && (
              <a
                className="fr-btn fr-btn--secondary fr-btn--sm fr-btn--icon-left fr-icon-google-fill fr-mt-2w"
                href={GOOGLE_PREFERRED_SOURCE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ajouter à vos sources préférées sur Google
                <span className="fr-sr-only"> - nouvelle fenêtre</span>
              </a>
            )}
          </div>
        </div>
        <div className="fr-footer__bottom">
          <ul className="fr-footer__bottom-list">
            <li className="fr-footer__bottom-item">
              <a className="fr-footer__bottom-link" href={PAGES.static.accessibilite.getPath()}>
                Accessibilité: partiellement conforme
              </a>
            </li>
            <li className="fr-footer__bottom-item">
              <a className="fr-footer__bottom-link" href={PAGES.static.politiqueConfidentialite.getPath()}>
                Politique de confidentialité
              </a>
            </li>
            <li className="fr-footer__bottom-item">
              <a className="fr-footer__bottom-link" href={PAGES.static.mentionsLegales.getPath()}>
                Mentions légales
              </a>
            </li>
            <li className="fr-footer__bottom-item">
              <a className="fr-footer__bottom-link" href={PAGES.static.cgu.getPath()}>
                Conditions générales d'utilisation
              </a>
            </li>
          </ul>
          <div className="fr-footer__bottom-copy">
            <p>
              Sauf mention contraire, tous les contenus de ce site sont sous{" "}
              <a href="https://github.com/etalab/licence-ouverte/blob/master/LO.md" target="_blank" rel="noopener noreferrer">
                licence etalab-2.0
                <span className="fr-sr-only"> - nouvelle fenêtre</span>
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
